import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { PremiumToast } from '@/components/Stripe/PremiumToast';
import { Text } from '@/components/Text';
import { useSubscription } from '@/hooks/useSubscription';
import { useCardStore } from '@/stores/useCardStore';
import { Link } from '@/types/link';
import { cn } from '@/utils/cn';
import { getIconForLink } from '@/utils/getIconForLink';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

interface IconCategoryProps {
  title: string;
  icons: Link[];
  selectedLink: Link | null;
  onLinkSelect: (link: Link) => void;
}

/**
 * Component for displaying a category of icons
 */
const IconCategory = ({
  title,
  icons,
  selectedLink,
  onLinkSelect,
}: IconCategoryProps) => {
  const { t } = useTranslation();
  const { card } = useCardStore();
  const { hasActiveSubscription } = useSubscription(card?.user || null);
  const navigate = useNavigate();

  // Get the IDs of links that are already used in the current card
  const usedLinkIds = card?.links?.map(cardLink => cardLink.link_id) || [];

  const handleIconClick = (icon: Link) => {
    // Check if the link is already used
    const isAlreadyUsed = usedLinkIds.includes(icon.id);

    if (isAlreadyUsed) {
      toast.error(t('updateCard:links.already_used'));
      return;
    }

    if (icon.is_premium && !hasActiveSubscription()) {
      toast.custom(toastInstance => (
        <PremiumToast toastInstance={toastInstance} navigate={navigate} />
      ));
    } else {
      onLinkSelect(icon);
    }
  };

  return (
    <>
      <Text className="mb-4 text-base font-medium">{title}</Text>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {icons.map(icon => {
          const isAlreadyUsed = usedLinkIds.includes(icon.id);

          return (
            <div
              key={icon.id}
              className="relative flex cursor-pointer flex-col"
              onClick={() => handleIconClick(icon)}
            >
              <div
                className={cn(
                  'group flex items-center rounded-lg bg-gray-100 p-2 transition-shadow',
                  isAlreadyUsed
                    ? 'cursor-not-allowed opacity-50 grayscale'
                    : 'hover:shadow-md',
                  selectedLink?.id === icon.id && !isAlreadyUsed
                    ? 'ring-1 ring-linko-light'
                    : !isAlreadyUsed && 'group-hover:bg-linko-light',
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center">
                    <Image
                      src={getIconForLink(icon.icon)}
                      alt={t(`icon:${icon.icon}`)}
                      className="size-6 rounded-[7px]"
                    />
                  </div>
                  <Text className="text-sm">{t(`icon:${icon.icon}`)}</Text>
                  {!!icon.is_premium && (
                    <span className="flex items-center justify-center rounded-full bg-gradient-to-r from-linko-light to-linko-dark px-1 py-0.5 text-[0.5rem] font-bold text-white shadow-sm">
                      PRO
                    </span>
                  )}
                  {isAlreadyUsed && (
                    <span className="flex items-center justify-center rounded-full bg-gray-400 px-1 py-0.5 text-[0.5rem] font-bold text-white shadow-sm">
                      {t('updateCard:links.used')}
                    </span>
                  )}
                </div>
                <div
                  className={cn(
                    'ml-auto rounded-full bg-white p-1.5 transition-colors',
                    !isAlreadyUsed && 'group-hover:bg-linko-light',
                  )}
                >
                  <FaPlus
                    className={cn(
                      'size-3',
                      isAlreadyUsed
                        ? 'text-gray-400'
                        : 'text-gray-light group-hover:text-primary',
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export interface IconSelectionViewProps {
  selectedLink: Link | null;
  onLinkSelect: (link: Link) => void;
  links: Link[];
}

export const IconSelectionView = ({
  selectedLink,
  onLinkSelect,
  links,
}: IconSelectionViewProps) => {
  const { t } = useTranslation();
  return (
    <div className="max-h-[70vh] overflow-y-auto">
      {/* Contact Icons */}
      <IconCategory
        title={t('updateCard:links.contact')}
        icons={links.filter(link => link.category === 'contact')}
        selectedLink={selectedLink}
        onLinkSelect={onLinkSelect}
      />

      {/* Social Media Icons */}
      <IconCategory
        title={t('updateCard:links.social_media')}
        icons={links.filter(link => link.category === 'social_media')}
        selectedLink={selectedLink}
        onLinkSelect={onLinkSelect}
      />

      {/* Other Platforms */}
      <IconCategory
        title={t('updateCard:links.other_platforms')}
        icons={links.filter(link => link.category === 'other_platforms')}
        selectedLink={selectedLink}
        onLinkSelect={onLinkSelect}
      />

      {/* Call to action pour suggérer un nouveau type de lien */}
      <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <Text className="mb-2 text-base font-semibold text-gray-700">
          {t('updateCard:links.suggest_new_type_title')}
        </Text>
        <Text className="mb-4 text-sm text-gray-500">
          {t('updateCard:links.suggest_new_type_desc')}
        </Text>
        <Button
          className="bg-linko-light"
          onClick={() =>
            window.open(
              'mailto:info@linko-connect.com?subject=Suggestion%20nouveau%20type%20de%20lien',
              '_blank',
            )
          }
        >
          {t('updateCard:links.suggest_new_type_button')}
        </Button>
      </div>
    </div>
  );
};
